import crossFetch from 'cross-fetch';
import {
  stringifyEntityRef,
  CompoundEntityRef,
} from '@backstage/catalog-model';
import { CodeOwnershipApi } from './CodeOwnershipApi';
import { ResponseError } from '@backstage/errors';
import {
  codeOwnershipResultsApiResponseSchema,
  codeOwnershipStatsApiResponseSchema,
} from '../types';

// these are isomorphic types that don't require @backstage/core-plugin-api
type DiscoveryApi = { getBaseUrl(pluginId: string): Promise<string> };
type FetchApi = { fetch: typeof fetch };

export class CodeOwnershipClient implements CodeOwnershipApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  public constructor(deps: {
    discoveryApi: DiscoveryApi;
    fetchApi?: FetchApi;
  }) {
    const { discoveryApi, fetchApi } = deps;
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi ?? { fetch: crossFetch };
  }

  public async getCodeOwnershipResults(options: {
    entityRef: CompoundEntityRef;
    ownerRefs?: CompoundEntityRef[];
    path?: string;
    basename?: string;
    limit?: number;
  }) {
    const { entityRef, ownerRefs, path, basename, limit } = options;
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      new URL(`${baseUrl}/query`).toString(),
      {
        method: 'POST',
        body: JSON.stringify({
          entityRef: stringifyEntityRef(entityRef),
          ownerRefs: ownerRefs
            ? ownerRefs?.map(r => stringifyEntityRef(r))
            : undefined,
          path,
          basename,
          limit,
        }),
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
      },
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const body = codeOwnershipResultsApiResponseSchema.parse(
      await response.json(),
    );
    return body.results;
  }

  public async getCodeOwnershipStats(options: {
    entityRef: CompoundEntityRef;
    ownerRefs?: CompoundEntityRef[];
    globs: string[];
  }) {
    const { entityRef, ownerRefs, globs } = options;
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      new URL(`${baseUrl}/stats`).toString(),
      {
        method: 'POST',
        body: JSON.stringify({
          entityRef: stringifyEntityRef(entityRef),
          ownerRefs: ownerRefs
            ? ownerRefs?.map(r => stringifyEntityRef(r))
            : undefined,
          globs,
        }),
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
      },
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const body = codeOwnershipStatsApiResponseSchema.parse(
      await response.json(),
    );
    return body.stats;
  }

  private async getBaseUrl() {
    return this.discoveryApi.getBaseUrl('code-ownership');
  }
}
